import { Fragment } from 'react'

import { linkifySpans } from '../lib/linkify'

// ============================================================================
// LINKIFIED TEXT — the render half of lib/linkify.ts. Read that file's header for why the
// linkifying happens here and not in the Worker's HTML sanitizer.
//
// NO HTML CROSSES THIS BOUNDARY. The input is a string, the output is React elements, and every
// character of the text still goes through React's own escaping. `dangerouslySetInnerHTML` is not
// used, imported, or needed — which is the property that makes it safe to point this at a body a
// professor pasted out of Word.
//
// THE LINK IDIOM is the app's own: underlined at `underline-offset-2`, in `info` — the blue the
// Connections screen already uses for a link (index.css, `--color-source-web`'s comment) — and one
// step darker on hover. Tokens only; no raw hex.
// ============================================================================

/** `target="_blank"` and the rel that goes with it, in one place so neither can be forgotten. */
const EXTERNAL = { target: '_blank', rel: 'noopener noreferrer' } as const

export function LinkifiedText({
  text,
  className,
}: {
  text: string
  /** Applied to the wrapping element, which is the one carrying the paragraph's own type. */
  className?: string
}) {
  const spans = linkifySpans(text)

  return (
    <span className={className}>
      {spans.map((span, i) =>
        span.kind === 'text' ? (
          <Fragment key={i}>{span.text}</Fragment>
        ) : (
          <a
            key={i}
            href={span.href}
            // THE FULL URL, ALWAYS. `label` may be elided in the middle; the title is what makes
            // that a fold rather than a concealment — hover and the whole address is there.
            title={span.href}
            {...EXTERNAL}
            // Defensive, and cheap: every current caller renders this OUTSIDE its row's own
            // button (the feed's expanded panel is the button's sibling, not its child, which is
            // what keeps the markup valid). If a later caller nests one, following the link must
            // still not also toggle the row it sits in.
            onClick={(event) => event.stopPropagation()}
            className="font-medium text-info underline underline-offset-2 transition-colors duration-[var(--dur-state)] ease-[var(--ease-ink)] hover:text-info-2"
          >
            {span.label}
          </a>
        ),
      )}
    </span>
  )
}
